import { useEffect, useState } from "react";
import useSWR from "swr";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createPaginationStore,
  DataTablePagination,
} from "@/components/zthd/data-table-pagination-mysql";
import { RefreshButton } from "@/components/list/refresh-button";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import { ROW_CARD_STATIC_CLASS } from "@/components/list/row-card";
import { Chip } from "@/components/badge/chip";
import { formatSize } from "@/components/versions/artifacts-card";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { fetcher, swrKey } from "@/lib/swr";
import { DATE_FULL } from "@/lib/date";

// App-wide artifacts inventory: every version's ready builds flattened into
// one newest-first page (the version card stays the per-version view).
interface AppArtifact {
  id: string;
  versionId: string;
  fileName: string;
  size: number;
  uploaderId: string | null;
  uploaderName?: string;
  channelId?: number | null;
  channelName?: string;
  versionName?: string;
  versionCode?: string;
  createdAt: string;
}

interface Channel {
  id: number;
  name: string;
}

const usePaginationStore = createPaginationStore(0, 50, "app-artifacts");

const ArtifactRow = ({
  artifact,
  pid,
}: {
  artifact: AppArtifact;
  pid: string;
}) => {
  const { toast } = useToast();

  // Machine uploads (Jenkins CI) have no user identity: null uploader.
  const uploader =
    artifact.uploaderName ?? (artifact.uploaderId ? undefined : "Jenkins");

  // Signed URLs are short-lived: request one per click, then open it.
  // Safari blocks window.open called after an await (not a direct user
  // gesture anymore): open the tab synchronously, then point it at the URL.
  const download = async () => {
    const win = window.open("", "_blank");
    try {
      const res = await fetcher<{ url: string }>([
        `/versions/${artifact.versionId}/artifacts/${artifact.id}/download-url`,
      ]);
      if (win) win.location.href = res.url;
      else window.location.href = res.url; // popup blocked: same-tab download
    } catch (error) {
      win?.close();
      toastError(toast, "Download failed", error);
    }
  };

  return (
    <div className={cn(ROW_CARD_STATIC_CLASS, "gap-2")}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium" title={artifact.fileName}>
            {artifact.fileName}
          </span>
          {artifact.channelId != null && (
            <Chip>{artifact.channelName ?? `Channel #${artifact.channelId}`}</Chip>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {formatSize(artifact.size)}
          {uploader ? ` · ${uploader}` : ""}
          {` · ${format(new Date(artifact.createdAt), DATE_FULL)}`}
        </div>
      </div>
      <Link
        to={`/apps/${pid}/versions/${artifact.versionId}`}
        className="shrink-0 text-xs text-muted-foreground hover:text-foreground hover:underline"
      >
        {artifact.versionName ?? "version"}
        {artifact.versionCode ? ` (${artifact.versionCode})` : ""}
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        title="Download"
        onClick={download}
      >
        <DownloadIcon />
      </Button>
    </div>
  );
};

const AppArtifacts = () => {
  const { id } = useParams();
  // Channel filter — only offered on channel apps; "all" keeps generic builds
  // and every flavor in one timeline.
  const [channel, setChannel] = useState("all");
  const paginationStore = usePaginationStore();
  const { pageIndex, pageSize } = paginationStore;
  const setPageIndex = usePaginationStore((state) => state.setPageIndex);

  useEffect(() => {
    setPageIndex(0);
  }, [id, channel, setPageIndex]);

  // Same key as the version detail page's channels fetch, so SWR dedupes.
  const { data: channels } = useSWR<Channel[]>(
    id ? swrKey("/channels", { pid: id }) : null
  );
  const hasChannels = (channels?.length ?? 0) > 0;

  const params: Record<string, unknown> = { pid: id, pageIndex, pageSize };
  if (channel !== "all") params.channelId = Number(channel);
  const { data, isLoading, isValidating, mutate } = useSWR<{
    data: AppArtifact[];
    total: number;
  }>(id ? swrKey("/artifacts", params) : null, { keepPreviousData: true });
  const artifacts: AppArtifact[] = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full items-center">
        {hasChannels && (
          <Select value={channel} onValueChange={setChannel}>
            <SelectTrigger size="sm" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {(channels ?? []).map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex space-x-2">
          <RefreshButton
            spinning={isValidating}
            onClick={() => mutate()}
          />
        </div>
      </div>
      {isLoading && !data && <ListLoading>Loading artifacts…</ListLoading>}
      {!isLoading && artifacts.length === 0 && (
        <ListEmpty>No artifacts. Builds uploaded to versions show here.</ListEmpty>
      )}
      <div className="flex flex-col gap-1">
        {artifacts.map((a) => (
          <ArtifactRow key={a.id} artifact={a} pid={String(id)} />
        ))}
      </div>
      <DataTablePagination total={data?.total ?? 0} store={paginationStore} />
    </div>
  );
};

export default AppArtifacts;
